import { SortingFn } from '@tanstack/react-table';
import { TopAdData } from '../../hooks/use-social-ads-overview-tab';
import { AdsRowType, ADS_ROW_TYPE } from '../../social-ads-top-ads';

export interface AdsColumnDefinitionInput {
  id: string;
  header: string;
  enableSorting: boolean;
  enableGlobalFiltering?: boolean;
  hidden: boolean;
  borderColor?: string;
  greyBackground?: boolean;
  onToggleClick?: () => void;
  isColumnExpanded?: boolean;
  isExpandableColumn?: boolean;
  size?: number;
  minSize?: number;
  maxSize?: number;
  rowType: AdsRowType;
  subColumns?: AdsColumnDefinitionInput[];
  order?: number;
  isFixed?: boolean;
  accessorFn?: (row: TopAdData) => unknown;
  tooltip?: string;
}

// Default column order for Social Ads Top Ads table
const baseDefaultColumnIds = [
  'by.name',
  'by.id',
  'metrics.roas',
  'metrics.impressions',
  'metrics.reach',
  'metrics.clicks',
  'metrics.ctr',
  'metrics.cpm',
  'metrics.cpc',
  'metrics.spend.amount',
  'metrics.purchase.amount',
];

export const getDefaultVisibleAdsColumnIds = (): string[] => {
  return baseDefaultColumnIds;
};

export const defaultAdsColumns: AdsColumnDefinitionInput[] = [
  {
    header: 'Ad',
    id: 'by.name',
    enableSorting: true,
    enableGlobalFiltering: true,
    rowType: ADS_ROW_TYPE.AD_NAME,
    hidden: false,
    size: 200,
    minSize: 150,
    maxSize: 300,
    order: 1,
    isFixed: true,
    accessorFn: (row: TopAdData) => {
      if (row.by?.__typename === 'MetaAd' && 'name' in row.by) {
        return row.by.name;
      }
      if (row.by?.__typename === 'MetaAdAccount' && 'name' in row.by) {
        return row.by.name;
      }
      if (row.by?.__typename === 'MetaAdCampaign' && 'name' in row.by) {
        return row.by.name;
      }
      if (row.by?.__typename === 'MetaAdSet' && 'name' in row.by) {
        return row.by.name;
      }
      return '';
    },
  },
  {
    header: 'Social handle',
    id: 'by.id',
    enableSorting: true,
    enableGlobalFiltering: true,
    rowType: ADS_ROW_TYPE.SOCIAL_ACCOUNT,
    hidden: false,
    size: 300,
    minSize: 200,
    maxSize: 400,
    order: 2,
    accessorFn: (row: TopAdData) => {
      if (row.by?.__typename === 'MetaAd' && 'id' in row.by) {
        return row.by.id;
      }
      if (row.by?.__typename === 'MetaAdAccount' && 'id' in row.by) {
        return row.by.id;
      }
      if (row.by?.__typename === 'MetaAdCampaign' && 'id' in row.by) {
        return row.by.id;
      }
      if (row.by?.__typename === 'MetaAdSet' && 'id' in row.by) {
        return row.by.id;
      }
      if (row.by?.__typename === 'MetaSocialAccount' && 'id' in row.by) {
        return row.by.id;
      }
      return '';
    },
  },
  {
    header: 'ROAS',
    id: 'metrics.roas',
    enableSorting: true,
    enableGlobalFiltering: true,
    rowType: ADS_ROW_TYPE.ROAS,
    hidden: false,
    size: 100,
    minSize: 80,
    maxSize: 120,
    order: 3,
    borderColor: 'hsl(var(--refresh-green-4))',
    accessorFn: (row: TopAdData) => row.metrics?.roas,
  },
  {
    header: 'Impressions',
    id: 'metrics.impressions',
    enableSorting: true,
    enableGlobalFiltering: true,
    rowType: ADS_ROW_TYPE.NUMBER,
    hidden: false,
    size: 130,
    minSize: 100,
    maxSize: 150,
    order: 4,
    borderColor: 'hsl(var(--refresh-blue-4))',
    accessorFn: (row: TopAdData) => row.metrics?.impressions,
  },
  {
    header: 'Reach',
    id: 'metrics.reach',
    enableSorting: true,
    enableGlobalFiltering: true,
    rowType: ADS_ROW_TYPE.NUMBER,
    hidden: false,
    size: 130,
    minSize: 100,
    maxSize: 150,
    order: 5,
    borderColor: 'hsl(var(--refresh-blue-3))',
    accessorFn: (row: TopAdData) => row.metrics?.reach,
  },
  {
    header: 'Clicks',
    id: 'metrics.clicks',
    enableSorting: true,
    enableGlobalFiltering: true,
    rowType: ADS_ROW_TYPE.NUMBER,
    hidden: false,
    size: 100,
    minSize: 80,
    maxSize: 120,
    order: 6,
    borderColor: 'hsl(var(--refresh-orange-3))',
    accessorFn: (row: TopAdData) => row.metrics?.clicks,
  },
  {
    header: 'CTR',
    id: 'metrics.ctr',
    enableSorting: true,
    enableGlobalFiltering: true,
    rowType: ADS_ROW_TYPE.PERCENTAGE,
    hidden: false,
    size: 100,
    minSize: 80,
    maxSize: 120,
    order: 7,
    borderColor: 'hsl(var(--refresh-teal-3))',
    accessorFn: (row: TopAdData) => row.metrics?.ctr,
  },
  {
    header: 'CPM',
    id: 'metrics.cpm',
    enableSorting: true,
    enableGlobalFiltering: true,
    rowType: ADS_ROW_TYPE.CURRENCY,
    hidden: false,
    size: 100,
    minSize: 80,
    maxSize: 120,
    order: 8,
    borderColor: 'hsl(var(--refresh-purple-3))',
    accessorFn: (row: TopAdData) => row.metrics?.cpm,
  },
  {
    header: 'CPC',
    id: 'metrics.cpc',
    enableSorting: true,
    enableGlobalFiltering: true,
    rowType: ADS_ROW_TYPE.CURRENCY,
    hidden: false,
    size: 100,
    minSize: 80,
    maxSize: 120,
    order: 9,
    borderColor: 'hsl(var(--refresh-purple-2))',
    accessorFn: (row: TopAdData) => row.metrics?.cpc,
  },
  {
    header: 'Spend',
    id: 'metrics.spend.amount',
    enableSorting: true,
    enableGlobalFiltering: true,
    rowType: ADS_ROW_TYPE.CURRENCY,
    hidden: false,
    size: 120,
    minSize: 100,
    maxSize: 140,
    order: 10,
    borderColor: 'hsl(var(--refresh-red-4))',
    accessorFn: (row: TopAdData) => row.metrics?.spend?.amount,
  },
  {
    header: 'Sales',
    id: 'metrics.purchase.amount',
    enableSorting: true,
    enableGlobalFiltering: true,
    rowType: ADS_ROW_TYPE.CURRENCY,
    hidden: false,
    size: 120,
    minSize: 100,
    maxSize: 140,
    order: 11,
    borderColor: 'hsl(var(--refresh-green-3))',
    accessorFn: (row: TopAdData) => row.metrics?.purchase?.amount,
  },
];

// Mapped column interface for table rendering
export interface AdsMappedColumn {
  id: string;
  enableSorting: boolean;
  enableGlobalFilter: boolean;
  sortingFn: SortingFn<TopAdData>;
  meta: {
    greyBackground?: true;
    headerGroupBorder: {
      borderColor?: string;
    };
    onToggleClick: (() => void) | null;
    isColumnExpanded?: boolean;
    isExpandableColumn?: boolean;
    draggable?: boolean;
  };
  header: string;
  rowType: AdsRowType;
  size?: number;
  minSize?: number;
  maxSize?: number;
  accessorFn?: (row: TopAdData) => unknown;
  isSubColumn?: boolean;
}
